import React, { useState } from 'react';
import {
  AutoComplete,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  Box,
  Text,
  SearchIcon,
  useToast,
} from '@razorpay/blade/components';
import { RegisterGenericType } from 'forms/useRegister';
import api from 'api';
import useReduxSelector from 'utils/useReduxSelector';
import { useQuery } from 'react-query';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { AppError } from 'utils/AppError';

interface SelectManagerProps {
  formData: Partial<{
    manager_people_id: number | null;
  }>;
  registerFormData: RegisterGenericType<
    Partial<{
      manager_people_id: number | null;
    }>
  >;
  managerName?: string;
}

export const SelectManager: React.FC<SelectManagerProps> = ({
  formData,
  registerFormData,
  managerName,
}) => {
  const [selectedManagerName, setSelectedManagerName] = useState(managerName || '');
  const [searchState, setSearchState] = useState(managerName || '');
  const toast = useToast();

  const orgId = useReduxSelector(
    (rootState) => rootState.loggedInUser.data?.currentOrganization?.id as number,
  );

  const { debouncedValue: debouncedSearchValue } = useDebouncedValue(searchState);

  const { data: managers } = useQuery({
    queryKey: ['search-people', debouncedSearchValue],
    queryFn: () =>
      api.people.search({
        search: debouncedSearchValue,
        orgId,
        onlyActive: true,
      }),
    enabled: !!debouncedSearchValue,
    select: (response) => response.map((person) => ({ id: person.key, name: person.label })),
    onError: (e: typeof AppError) => {
      toast.show({
        content: 'Failed to fetch managers',
        color: 'negative',
      });
    },
  });

  const handleInputValueChange = ({ value }: { value?: string }) => {
    setSearchState(value || '');
  };

  return (
    <Dropdown
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSearchState(selectedManagerName || managerName || '');
        }
      }}>
      <AutoComplete
        label="Manager"
        placeholder="Eg. Mark Thompson (457)"
        inputValue={searchState}
        onInputValueChange={handleInputValueChange}
        filteredValues={managers?.map((manager) => manager.id.toString()) || []}
        {...registerFormData('manager_people_id', 'select')}
      />
      {searchState && (
        <DropdownOverlay>
          {managers && managers.length > 0 ? (
            <ActionList>
              {managers.map((manager) => (
                <ActionListItem
                  key={manager.id}
                  title={manager.name}
                  value={manager.id.toString()}
                  isSelected={formData.manager_people_id === manager.id}
                  onClick={() => {
                    setSelectedManagerName(manager.name);
                  }}
                />
              ))}
            </ActionList>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="spacing.5"
              padding="spacing.5">
              <SearchIcon size="xlarge" color="surface.icon.gray.muted" />
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Text size="medium" weight="semibold" textAlign="center">
                  No Manager Found
                </Text>
                <Text
                  size="small"
                  weight="regular"
                  color="surface.text.gray.subtle"
                  textAlign="center">
                  Try searching with a different name
                </Text>
              </Box>
            </Box>
          )}
        </DropdownOverlay>
      )}
    </Dropdown>
  );
};
